import { booleanColumn } from '@/shared/grids/columns/booleanColumn';
import { dateColumn } from '@/shared/grids/columns/dateColumn';
import { idColumn } from '@/shared/grids/columns/idColumn';
import { notesExcerptColumn } from '@/shared/grids/columns/notesExcerptColumn';
import { notesIconColumn } from '@/shared/grids/columns/notesIconColumn';
import { textColumn } from '@/shared/grids/columns/textColumn';
import { ClientType, RecurringTaskStatus } from '@/shared/types/enum';
import { GridSettings } from '@/shared/types/grid';
import { ScheduleDay } from '@/shared/types/schedule';
import { enumOptions } from '@/shared/utils/enum';
import { formatEnum } from '@/shared/utils/format';

import { RecurringTaskView } from '@prisma/client';

export const recurringTaskGrid: GridSettings<RecurringTaskView & { scheduleDaysDescription: string }> = {
  extraSelect: ['scheduleDay'],
  adapter: (task) => {
    if (task.scheduleDay) {
      task.scheduleDaysDescription = formatEnum(ScheduleDay, task.scheduleDay);
    }
  },
  defaultFilters: {
    status: {
      operator: 'notequal',
      value: RecurringTaskStatus.Completed,
    },
  },
  defaultSort: {
    task: 'asc',
  },
  extraSort: {
    clientName: 'asc',
    task: 'asc',
  },
  columns: [
    idColumn(),
    notesIconColumn({
      field: 'hasNotes',
    }),
    textColumn({
      header: 'Task',
      field: 'task',
      width: 300,
    }),
    textColumn({
      header: 'Service',
      field: 'serviceId',
      width: 200,
      extra: {
        displayField: 'serviceName',
        sortField: 'serviceName',
        filter: {
          lookup: 'Service',
        },
      },
    }),
    textColumn({
      header: 'Type',
      field: 'clientType',
      visible: false,
      extra: {
        displayField: 'clientTypeName',
        sortField: 'clientTypeName',
        filter: {
          options: enumOptions(ClientType),
        },
      },
    }),
    textColumn({
      header: 'Client',
      field: 'clientId',
      width: 300,
      extra: {
        displayField: 'clientName',
        sortField: 'clientName',
        filter: {
          lookup: 'ClientView',
        },
      },
    }),
    textColumn({
      header: 'First Name',
      field: 'clientFirstName',
      visible: false,
    }),
    textColumn({
      header: 'Last Name',
      field: 'clientLastName',
      visible: false,
    }),
    textColumn({
      header: 'Registration Number',
      field: 'clientCompanyRegistrationNo',
      visible: false,
    }),
    textColumn({
      header: 'UTR',
      field: 'clientUtr',
      visible: false,
    }),
    booleanColumn({
      header: 'Records Required',
      field: 'recordsRequired',
      iconClass: 'fa-solid fa-folder-closed',
    }),
    textColumn({
      header: 'Schedule',
      field: 'scheduleDescription',
      emptyText: 'One Off',
      emptyClass: 'text-more-muted',
      width: 120,
    }),
    textColumn({
      header: 'Day',
      field: 'scheduleDaysDescription',
      emptyText: 'Any',
      allowFiltering: false,
      allowSorting: false,
      width: 100,
    }),
    dateColumn({
      header: 'From',
      field: 'startFromDate',
    }),
    dateColumn({
      header: 'Until',
      field: 'continueUntilDate',
      emptyText: 'Ongoing',
    }),
    dateColumn({
      header: 'Added',
      field: 'createdTime',
      visible: false,
    }),
    textColumn({
      header: 'Added By',
      field: 'createdByUserId',
      visible: false,
      extra: {
        displayField: 'createdByUserName',
        sortField: 'createdByUserName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    textColumn({
      header: 'Assigned To',
      field: 'assignedToUserId',
      emptyText: 'Not assigned',
      extra: {
        displayField: 'assignedToUserName',
        sortField: 'assignedToUserName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    notesExcerptColumn({
      field: 'notesExcerpt',
    }),
    textColumn({
      header: 'Status',
      field: 'status',
      textAlign: 'Center',
      template: `<span class="task-status task-status-\${window.formatters.formatClassName(status)}">\${status}</span>`,
      visible: false,
      extra: {
        filter: {
          options: enumOptions(RecurringTaskStatus),
        },
      },
    }),
  ],
};
